import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Empty,
  Form,
  InputNumber,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { organizationsApi } from '../organizations.api';
import { orgsQueryKey, useActiveOrg } from '../active-org-context';
import { SUPPORTED_CURRENCIES, type CurrencyCode } from '../types';

/**
 * Organization-wide settings: reporting currency and the default monthly budget
 * caps applied to every project (a project can still override its own caps).
 */
export function OrgSettingsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { activeOrg, activeOrgId } = useActiveOrg();
  const canEdit = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [aiCap, setAiCap] = useState<number | null>(null);
  const [adCap, setAdCap] = useState<number | null>(null);

  useEffect(() => {
    if (activeOrg) {
      setCurrency(activeOrg.settings.currency);
      setAiCap(activeOrg.settings.defaultMonthlyAiTokenCap);
      setAdCap(activeOrg.settings.defaultMonthlyAdSpendCap);
    }
  }, [activeOrg]);

  const billingKey = ['billing', activeOrgId] as const;
  const billing = useQuery({
    queryKey: billingKey,
    queryFn: () => organizationsApi.getBilling(activeOrgId!),
    enabled: Boolean(activeOrgId),
  });
  const isOwner = activeOrg?.role === 'owner';
  const changePlan = useMutation({
    mutationFn: (plan: string) => organizationsApi.setPlan(activeOrgId!, plan),
    onSuccess: (data) => {
      queryClient.setQueryData(billingKey, data);
      message.success(`Switched to the ${data.plan.name} plan`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to change plan'),
  });

  const save = useMutation({
    mutationFn: () =>
      organizationsApi.updateSettings(activeOrgId!, {
        currency,
        defaultMonthlyAiTokenCap: aiCap,
        defaultMonthlyAdSpendCap: adCap,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgsQueryKey });
      message.success('Organization settings saved');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to save settings'),
  });

  if (!activeOrg) return <Empty description="Select an organization" />;

  return (
    <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
      <Typography.Title level={3}>Organization settings</Typography.Title>
      <Typography.Paragraph type="secondary">
        Global defaults for <strong>{activeOrg.name}</strong>. Budget caps are applied per project
        each month; a project can override them under its Campaigns tab.
      </Typography.Paragraph>

      <Card title="Plan & billing" loading={billing.isLoading} style={{ marginBottom: 24 }}>
        {billing.data ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size="large" wrap>
              <Statistic
                title="Current plan"
                value={billing.data.plan.name}
                prefix={<Tag color="blue">{billing.data.plan.id}</Tag>}
              />
              <Statistic
                title="Projects"
                value={`${billing.data.usage.projects} / ${billing.data.plan.maxProjects ?? '∞'}`}
              />
              <Statistic
                title="Seats"
                value={`${billing.data.usage.seats} / ${billing.data.plan.seats ?? '∞'}`}
              />
            </Space>
            <Space wrap>
              {billing.data.plans.map((p) => (
                <Button
                  key={p.id}
                  type={p.id === billing.data.plan.id ? 'primary' : 'default'}
                  disabled={!isOwner || p.id === billing.data.plan.id}
                  loading={changePlan.isPending && changePlan.variables === p.id}
                  onClick={() => changePlan.mutate(p.id)}
                >
                  {p.name} · ${p.priceUsd}/mo
                </Button>
              ))}
            </Space>
            {!isOwner ? (
              <Typography.Text type="secondary">
                Only the owner can change the plan.
              </Typography.Text>
            ) : null}
          </Space>
        ) : null}
      </Card>

      <Card>
        <Form layout="vertical">
          <Form.Item label="Reporting currency">
            <Select<CurrencyCode>
              style={{ width: 200 }}
              value={currency}
              disabled={!canEdit}
              onChange={(v) => setCurrency(v)}
              options={SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: c }))}
            />
          </Form.Item>

          <Form.Item
            label="Default monthly AI token cap"
            help="Applies to projects without their own cap. Leave empty for unlimited."
          >
            <InputNumber
              style={{ width: 200 }}
              min={0}
              value={aiCap}
              placeholder="unlimited"
              disabled={!canEdit}
              onChange={(v) => setAiCap(typeof v === 'number' ? v : null)}
            />
          </Form.Item>

          <Form.Item
            label={`Default monthly ad spend cap (${currency})`}
            help="Applies to projects without their own cap. Leave empty for unlimited."
          >
            <InputNumber
              style={{ width: 200 }}
              min={0}
              value={adCap}
              placeholder="unlimited"
              disabled={!canEdit}
              onChange={(v) => setAdCap(typeof v === 'number' ? v : null)}
            />
          </Form.Item>

          {canEdit ? (
            <Space>
              <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
                Save settings
              </Button>
            </Space>
          ) : (
            <Typography.Text type="secondary">
              Only owners and admins can change organization settings.
            </Typography.Text>
          )}
        </Form>
      </Card>
    </div>
  );
}
